/**
 * KAN-782: a caption above a filter control. Several selects in a row all
 * read "Все ..." when nothing is chosen, so without a caption the user cannot
 * tell which filter is which. The caption is visual only: the control keeps
 * its own aria-label, so the accessible name still includes the chosen value.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Trigger width of a captioned filter select: full width on phones, on wide
 * screens it grows to its longest value ("Все источники", "Все менеджеры")
 * instead of clipping it in a fixed width.
 */
export const FILTER_TRIGGER_CLASS = 'w-full sm:w-auto sm:min-w-[9rem]';

/**
 * KAN-906: compact filter row (CRM table): 32px controls, no caption above,
 * the name goes inside the control (aria-label stays).
 */
export const COMPACT_TRIGGER_CLASS = 'h-8 w-auto min-w-[8rem] text-sm';

interface FilterFieldProps {
  label: string;
  children: ReactNode;
  className?: string;
  /** KAN-906: no visible caption (compact row): only the control is rendered. */
  hideLabel?: boolean;
}

export function FilterField({ label, children, className, hideLabel = false }: FilterFieldProps): JSX.Element {
  if (hideLabel) return <>{children}</>;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="text-xs text-gray-500">{label}</span>
      {children}
    </div>
  );
}
